import * as React from 'react';

import { cn } from './cn';

// ButtonGroup is a cooperative composition: it advertises orientation and the
// position (first / middle / last / only) of each child via React context. The
// children — typically Buttons — opt in by reading `useButtonGroupContext()`
// and adjusting their own `rounded-*` corners. There is no specificity war
// between the parent and the child class lists, and no `!important` needed.

const ButtonGroupContext = React.createContext(null);

const useButtonGroupContext = () => React.useContext(ButtonGroupContext);

const resolvePosition = (index, count) => {
    if (count === 1) {
        return 'only';
    }

    if (index === 0) {
        return 'first';
    }

    if (index === count - 1) {
        return 'last';
    }

    return 'middle';
};

// Children read this to flatten the corners AND drop the leading-side
// border that abuts a sibling. Each junction is drawn by a single
// border (the previous sibling's trailing edge) — adjacent siblings
// stop painting their leading-side border so the line never doubles
// up. Returns `null` when the child is standalone or sits alone in
// the group: keep the size variant's rounding and full border.
const resolveGroupedCornerClassName = (groupContext) => {
    if (!groupContext) {
        return null;
    }

    const { orientation, position } = groupContext;

    if (position === 'only') {
        return null;
    }

    if (orientation === 'vertical') {
        if (position === 'first') {
            return 'tw-rounded-b-none';
        }

        if (position === 'last') {
            return 'tw-rounded-t-none tw-border-t-0';
        }

        return 'tw-rounded-none tw-border-t-0';
    }

    if (position === 'first') {
        return 'tw-rounded-r-none';
    }

    if (position === 'last') {
        return 'tw-rounded-l-none tw-border-l-0';
    }

    return 'tw-rounded-none tw-border-l-0';
};

const ButtonGroup = React.forwardRef(({ children, className: _className, style: _style, orientation = 'horizontal', ...props }, ref) => {
    const validChildren = React.Children.toArray(children).filter(React.isValidElement);

    return (
        <div
            aria-orientation={orientation}
            className={cn(
                'tw-inline-flex tw-w-fit tw-flex-none tw-items-stretch [&>*]:tw-flex-none [&>*]:tw-relative [&>*[aria-pressed=true]]:tw-z-10 [&>*:focus-visible]:tw-z-20',
                orientation === 'vertical' ? 'tw-flex-col' : 'tw-flex-row'
            )}
            ref={ref}
            role="group"
            {...props}
        >
            {validChildren.map((child, index) => {
                const position = resolvePosition(index, validChildren.length);

                return (
                    <ButtonGroupContext.Provider
                        key={child.key ?? index}
                        value={{ orientation, position }}
                    >
                        {child}
                    </ButtonGroupContext.Provider>
                );
            })}
        </div>
    );
});

ButtonGroup.displayName = 'ButtonGroup';

export { ButtonGroup, ButtonGroupContext, resolveGroupedCornerClassName, useButtonGroupContext };
